/**
 * Onay/Moderasyon Denetim Kaydı (Audit Log)
 * Her onay/red/doğrudan-düzenleme işlemini KİM, NE ZAMAN, NE değiştirdi bilgisiyle
 * değişmez (immutable) bir kayda yazar. "Hangi fotoğrafı yükledi/değiştirdi" sorusunu
 * yanıtlamak ve onay sonrası değişiklikleri izlenebilir kılmak için kullanılır.
 *
 * Koleksiyon: approvalLogs
 * Kurallar (firestore.rules): admin/moderator okuyup oluşturabilir; güncelleme kapalı; silme sadece admin.
 */
import { collection, addDoc, getDocs, query, orderBy, where, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

const COLLECTION = 'approvalLogs';

/** Denetlenen varlık tipleri */
export const AUDIT_ENTITY = {
  PRODUCT: 'product',
  SELLER: 'seller',
  PROFILE_IMAGE: 'profileImage',
  PROFILE_CHANGE: 'profileChange'
};

/** Denetlenen aksiyonlar */
export const AUDIT_ACTION = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  DIRECT_EDIT: 'direct_edit' // admin/moderator onaysız doğrudan düzenleme
};

/**
 * before/after objelerini karşılaştırıp değişen alanların listesini döndürür.
 * Dizi/obje alanları JSON ile sığ kıyaslanır (görsel listeleri için yeterli).
 * @param {Object} before
 * @param {Object} after
 * @param {string[]} [fields] - kontrol edilecek alanlar (yoksa iki objenin tüm anahtarları)
 * @returns {Array<{ field: string, before: any, after: any }>}
 */
export const diffFields = (before = {}, after = {}, fields = null) => {
  const keys = fields || Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  const changed = [];
  for (const key of keys) {
    const a = before ? before[key] : undefined;
    const b = after ? after[key] : undefined;
    let equal;
    if (typeof a === 'object' || typeof b === 'object') {
      equal = JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    } else {
      equal = a === b;
    }
    if (!equal) changed.push({ field: key, before: a ?? null, after: b ?? null });
  }
  return changed;
};

// Denetim kaydının Firestore 1MB doküman sınırını aşmaması için büyük alanları kırp.
const MAX_ARRAY_ITEMS = 30;
const MAX_STRING_LEN = 2000;
const capValue = (v) => {
  if (Array.isArray(v)) {
    return v.length > MAX_ARRAY_ITEMS
      ? { _truncated: true, count: v.length, sample: v.slice(0, MAX_ARRAY_ITEMS) }
      : v;
  }
  if (typeof v === 'string' && v.length > MAX_STRING_LEN) {
    return v.slice(0, MAX_STRING_LEN) + '…';
  }
  return v ?? null;
};
const capObject = (obj) => {
  if (!obj || typeof obj !== 'object') return obj ?? null;
  const out = {};
  for (const k of Object.keys(obj)) out[k] = capValue(obj[k]);
  return out;
};

/**
 * Bir onay/red/doğrudan-düzenleme işlemini denetim koleksiyonuna yazar.
 * Asla throw etmez; loglama başarısız olsa bile ana işlem akışını bozmaz.
 * @param {Object} params
 * @param {string} params.entityType - AUDIT_ENTITY
 * @param {string} params.entityId
 * @param {string} params.action - AUDIT_ACTION
 * @param {string} params.actorId - işlemi yapan uid
 * @param {string} [params.actorEmail]
 * @param {string[]} [params.actorRoles] - işlemi yapanın rolleri
 * @param {string} [params.sellerId] - varlığın sahibi (ürün/mağaza sahibi)
 * @param {Object} [params.before] - değişiklik öncesi anlamlı alanlar
 * @param {Object} [params.after] - değişiklik sonrası anlamlı alanlar
 * @param {string[]} [params.compareFields] - diff için bakılacak alanlar
 * @param {string} [params.note] - onay/red notu
 * @returns {Promise<{ success: boolean, id?: string, error?: string }>}
 */
export const recordApprovalLog = async ({
  entityType,
  entityId,
  action,
  actorId,
  actorEmail = null,
  actorRoles = [],
  sellerId = null,
  before = null,
  after = null,
  compareFields = null,
  note = null
} = {}) => {
  try {
    // Diff'i orijinal değerlerden hesapla (doğru), sonra saklanan değerleri kırp (boyut güvenliği)
    const changedFields = (before || after) ? diffFields(before || {}, after || {}, compareFields) : [];
    const safeChangedFields = changedFields.map((c) => ({
      field: c.field,
      before: capValue(c.before),
      after: capValue(c.after)
    }));
    const docRef = await addDoc(collection(db, COLLECTION), {
      entityType: entityType || null,
      entityId: entityId || null,
      action: action || null,
      actorId: actorId || null,
      actorEmail,
      actorRoles: Array.isArray(actorRoles) ? actorRoles : [],
      sellerId,
      before: capObject(before),
      after: capObject(after),
      changedFields: safeChangedFields,
      changedFieldNames: changedFields.map((c) => c.field),
      note: typeof note === 'string' ? note.trim() || null : null,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    // Denetim kaydı kritik değildir; sessizce uyar.
    logger.warn('⚠️ Denetim kaydı yazılamadı:', error?.message || error);
    return { success: false, error: error?.message };
  }
};

/**
 * Denetim kayıtlarını getirir (Admin paneli için). En yeni önce.
 * @param {Object} [opts]
 * @param {string} [opts.entityType] - tek bir varlık tipine filtrele
 * @param {string} [opts.entityId] - tek bir varlığa filtrele
 * @param {number} [opts.max=100]
 * @returns {Promise<{ success: boolean, data: Array, error?: string }>}
 */
export const getApprovalLogs = async ({ entityType = null, entityId = null, max = 100 } = {}) => {
  try {
    const ref = collection(db, COLLECTION);
    const clauses = [];
    if (entityType) clauses.push(where('entityType', '==', entityType));
    if (entityId) clauses.push(where('entityId', '==', entityId));
    const q = query(ref, ...clauses, orderBy('createdAt', 'desc'), limit(max));
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { success: true, data };
  } catch (error) {
    logger.error('❌ Denetim kayıtları getirilirken hata:', error);
    return { success: false, error: error?.message, data: [] };
  }
};
